
import { defineComponent } from 'vue'
import MobileFloatingNavigation from '@/components/common/MobileFloatingNavigation.vue'
import { fetchAndSaveBrandData, getMobileTheme, isClientPortal } from './helper'
import { fetchCat } from '@/helper/permission.helper'
import MobileAlertModal from '@/components/common/MobileAlertModal.vue'
import UISpinner from '@/components/common/UISpinner.vue'
import postscribe from 'postscribe'
import config from './config'
import axios from 'axios'
import { generateToken } from '../http-common'
import restAgent from './helper/restAgent'

export default defineComponent ({
  metaInfo() {
    const meta = {
      title: this.$store.getters.pageTitle,
      link: [
        {
          vmid: 'favicon',
          rel: 'shortcut icon',
          href: this.$store.getters.favicon,
        },
      ]
    }

    if (this.appManifest) {
      meta['link'].push(this.appManifest)
      meta['link'].push(...this.appleIcons)
      meta.title = ''
      meta['name'] = this.appleMeta
    }
    if (this.newMobileScreens) {
      meta['meta'] = [this.zoomDisableMeta]
    }
    
    return meta
  },
  components: {
    MobileFloatingNavigation,
    MobileAlertModal,
    UISpinner
  },
  beforeMount() {
    this.updateDevice()
    window.addEventListener('resize', this.updateDevice)
    this.checkLoginStatus()
  },
  unmounted() {
    this.emitter.off('handleUpsellCheckout')
  },
  async mounted() {
    this.emitter.on('handleUpsellCheckout', this.handleUpsellCheckout)
    this.fetching = true
    try {
      if (isClientPortal()) {
        await this.getUserDataFromClientPortal()
      }
      const brandData = await fetchAndSaveBrandData(this)

      if (!brandData) return
      // fetch portal settings data if not the cat
      const { siteInfo, builderSettings } = brandData

      if (siteInfo) {
        const { customJs, customCss, customHeader, customFooter } = siteInfo

        this.siteCustomJs = customJs || ''
        this.siteCustomCss = customCss || ''
        if (customHeader) {
          postscribe(`#siteCustomHeader`, customHeader)
        }
        if (customFooter) {
          postscribe(`#siteCustomFooter`, customFooter)
        }
      }

      if (builderSettings) {
        const { appSettings, pwaEnabled } = builderSettings
        if (!isClientPortal()) {
          this.manifestData = appSettings || null
        }
        if (this.manifestData && pwaEnabled) {
          this.manifestData.start_url =
            config.mode === 'dev'
              ? 'http://localhost:4040/'
              : `https://${window.location.hostname}/`
          this.manifestData.display = 'standalone'
          this.manifestData.offline_enabled = true
        }

        if (appSettings) {
          this.setPWAAppSettings({
            name: appSettings.name,
            logo:
              appSettings.icons && appSettings.icons.length > 0
                ? appSettings.icons[0].src
                : '',
            description: appSettings.description,
            pwaEnabled: builderSettings.pwaEnabled,
          })
        }

        this.localPWAFlag = pwaEnabled
        this.updateMobileScreens(pwaEnabled)
      }

      this.initiateCustomTags()
    } catch (error) {
      console.error('Error while fetching brand data --> ', error)
    } finally {
      this.fetching = false
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateDevice)
    this.removePWAEvent()
  },
  methods: {
    async getUserDataFromClientPortal() {
      this.fetchingClientPortal = true
      const cat = fetchCat()
      const token = await generateToken()
      const contactId = cat && cat.contactId ? cat.contactId : cat?.contact_id
      const locationId = cat && cat.locationId ? cat.locationId : cat?.location_id
      try {
        if (locationId && contactId) {
          const data = await restAgent.ClientPortal.getUserByContactId(locationId, contactId)
          this.$store.commit('SET_CLIENT_PORTAL_USER_DATA', data)
        }
      } catch (e) {
        console.log("Error while fetching client portal data: ", e)
      } finally {
        this.fetchingClientPortal = false
      }
    },
    initiateCustomTags() {
      this.addCustomCSSTag()
      this.addCustomJsTag()
    },
    addCustomCSSTag() {
      const customCss = document.createElement('style')
      customCss.setAttribute('type', 'text/css')
      customCss.setAttribute('vmid', 'site-custom-css')
      customCss.innerHTML = this.siteCustomCss
      document.head.appendChild(customCss)
    },
    addCustomJsTag() {
      const customScript = document.createElement('script')
      customScript.setAttribute('type', 'text/javascript')
      customScript.setAttribute('vmid', 'site-custom-js')
      customScript.innerHTML = this.siteCustomJs
      document.head.appendChild(customScript)
    },
    handleUpsellCheckout(upsellId: string) {
      const routeData = {
        name: isClientPortal() ? 'in-app-upsell-v2' : 'in-app-upsell',
        params: {
          id: upsellId,
        },
      }
      if (!this.newMobileScreens) {
        const routerData = this.$router.resolve(routeData)
        const openedWindow = window.open(routerData.href, '_blank')
        const timer = setInterval(async () => {
          if (openedWindow.closed) {
            clearInterval(timer)
            window.location.reload()
          }
        }, 500)
      } else {
        this.$router.push(routeData)
      }
    },
    updateDevice() {
      if (window.innerWidth <= 1024) {
        this.$store.commit('SET_DEVICE', 'mobile')
      } else {
        this.$store.commit('SET_DEVICE', 'desktop')
      }

      this.updateMobileScreens(this.localPWAFlag)
    },
    updateMobileScreens(pwaFlag) {
      this.$store.commit(
        'SHOW_NEW_MOBILE_SCREENS',
        this.device === 'mobile' && !this.isBuilder ? pwaFlag : false
      )
    },
    setPWAAppSettings(settings) {
      this.$store.commit('SET_PWA_APP_SETTINGS', settings)
    },
    removePWAEvent() {
      this.$store.commit('SET_PWA_APP_SETTINGS', {
        addWithExisting: true,
        pwaEvent: null,
      })
    },
    checkLoginStatus() {
      const cat = fetchCat()
      this.isLoggedIn = cat && (cat.contact_id || cat.contactId) ? true : false
    },
    getMobileTheme,
    isClientPortal,
  },
  data() {
    return {
      siteCustomJs: '',
      siteCustomCss: '',
      manifestData: null,
      localPWAFlag: false,
      fetching: false,
      isLoggedIn: false,
      fetchingClientPortal: false
    }
  },
  computed: {
    appManifest(): any {
      if (this.manifestData) {
        const data = JSON.stringify(this.manifestData)
        const blob = new Blob([data], { type: 'application/json' })
        const manifestURL = URL.createObjectURL(blob)
        return {
          vmid: 'manifest',
          rel: 'manifest',
          href: manifestURL,
        }
      }
      return null
    },
    appleMeta(): any {
      return [
        {
          vmid: 'apple-web-app-title',
          name: 'apple-mobile-web-app-title',
          content: this.manifestData.name,
        },
        {
          vmid: 'apple-web-app-capable',
          name: 'apple-mobile-web-app-capable',
          content: 'yes',
        },
      ]
    },
    appleIcons(): any {
      return this.manifestData.icons.map((icon, index) => {
        if (icon.src) {
          const appleIcon = {
            vmid: `apple-touch-icon-${index + 1}`,
            rel: 'apple-touch-icon',
            sizes: icon.sizes,
            href: icon.src,
          }
          return appleIcon
        }
      })
    },
    zoomDisableMeta() {
      return {
        name: 'viewport',
        content:
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0',
      }
    },
    device(): string {
      return this.$store.getters.device
    },
    newMobileScreens(): boolean {
      return this.$store.getters.newMobileScreens
    },
    isBuilder(): boolean {
      return this.$route.query.builder === 'true'
    },
    pwaAppSettings() {
      return this.$store.getters.pwaAppSettings
    },
  },
})
